'use client'

import { useEffect, useRef, useState } from 'react'

import { useSession } from 'next-auth/react'

import { useCampaignJoinFlow } from '../hooks/useCampaignJoinFlow'
import { mapCampaignJoinErrorMessage } from '../lib/campaign-join-errors'
import {
  CAMPAIGN_PAGE_VARIANT,
  formatCampaignPeriod
} from '../lib/campaign-ui'

import { AppLink, useAppRouter, useAppSearchParams } from '@/shared/lib/router'
import { Button } from '@/shared/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/shared/ui/Card'
import { NotFoundAlert } from '@/shared/ui/NotFoundAlert'
import { Page } from '@/shared/ui/Page'

import { CampaignListSkeleton } from './CampaignListSkeleton'

export function CampaignJoinPage() {
  const searchParams = useAppSearchParams()
  const code = searchParams.get('code')?.trim() ?? ''
  const router = useAppRouter()
  const { status } = useSession()
  const [message, setMessage] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)
  const hasConfirmed = useRef(false)

  const { previewQuery, joinMutation } = useCampaignJoinFlow(code, {
    enabled: status === 'authenticated'
  })
  const { data: preview, isLoading: isPreviewLoading } = previewQuery

  useEffect(() => {
    if (previewQuery.isError) {
      setMessage({
        type: 'error',
        text: mapCampaignJoinErrorMessage(previewQuery.error)
      })
    }
  }, [previewQuery.isError, previewQuery.error])

  useEffect(() => {
    if (!code) {
      setMessage({
        type: 'error',
        text: 'Informe um código de campanha válido na URL.'
      })
    }
  }, [code])

  if (status === 'unauthenticated' || status === 'loading') {
    return (
      <Page variant={CAMPAIGN_PAGE_VARIANT}>
        <Card variant={CAMPAIGN_PAGE_VARIANT} className="mx-auto w-full max-w-lg">
          <CardBody>
            <NotFoundAlert
              title="Faça login"
              text="Entre na sua conta para ingressar na campanha."
            />
          </CardBody>
        </Card>
      </Page>
    )
  }

  const campaign = preview?.campaign

  return (
    <Page variant={CAMPAIGN_PAGE_VARIANT}>
      <Card
        variant={CAMPAIGN_PAGE_VARIANT}
        showTrophies
        className="mx-auto w-full max-w-lg"
      >
        <CardHeader>
          <CardTitle>Ingresso na campanha</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {isPreviewLoading ? (
            <CampaignListSkeleton />
          ) : campaign ? (
            <>
              <div className="space-y-2 text-center">
                <h2 className="font-sans text-xl font-bold text-[#974319]">
                  {campaign.name}
                </h2>
                <p className="text-sm font-medium text-[#7F7FAD]">
                  {formatCampaignPeriod(campaign.starts_at, campaign.ends_at)}
                </p>
                {preview?.label ? (
                  <p className="inline-flex rounded-full bg-white/80 px-3 py-1 text-sm font-semibold text-jacaranda shadow-[0_2px_0_0_#f39d72]">
                    Convite: {preview.label}
                  </p>
                ) : null}
              </div>

              <Button
                className="w-full"
                disabled={joinMutation.isPending || hasConfirmed.current}
                onClick={() => {
                  hasConfirmed.current = true
                  joinMutation.mutate(undefined, {
                    onSuccess: record => {
                      setMessage({
                        type: 'success',
                        text: record.already_joined
                          ? 'Você já está inscrito nesta campanha.'
                          : 'Inscrição confirmada!'
                      })
                      router.push(`/campanhas/${record.campaign_id}`)
                    },
                    onError: error => {
                      setMessage({
                        type: 'error',
                        text: mapCampaignJoinErrorMessage(error, 'Não foi possível ingressar.')
                      })
                    }
                  })
                }}
              >
                {joinMutation.isPending ? 'Confirmando...' : 'Confirmar ingresso'}
              </Button>
            </>
          ) : (
            <NotFoundAlert
              title="Ops! Rota não encontrada 🚧"
              text="Essa campanha não está disponível no momento ou o link informado é inválido. Verifique o código com o seu professor ou tente novamente!"
            />
          )}

          {campaign && message?.type === 'error' ? (
            <p className="text-center text-sm font-semibold text-destructive">
              {message.text}
            </p>
          ) : null}

          <p className="text-center text-sm text-[#7F7FAD]">
            <AppLink
              href="/campanhas"
              className="font-semibold text-jacaranda hover:underline"
            >
              Ver minhas campanhas
            </AppLink>
          </p>
        </CardBody>
      </Card>
    </Page>
  )
}
